// App — the root component. Extracts the token from the URL, runs the
// confirmation state machine, and renders the matching screen.

import { useMemo, type ReactElement } from 'react';
import { BookingCard } from './components/BookingCard';
import { SiteFooter } from './components/SiteFooter';
import { SiteHeader } from './components/SiteHeader';
import {
  AlreadyDoneScreen,
  AllCaughtUpScreen,
  CancelledScreen,
  ErrorScreen,
  ExpiredScreen,
  LoadingScreen,
  NotFoundScreen,
  SubmittedScreen,
  SupersededScreen,
} from './components/StateScreens';
import { extractTokenFromPath, useConfirmation } from './hooks/useConfirmation';
import type { ConfirmState } from './types';

const HELPER_BY_STATE: Record<ConfirmState, string> = {
  ready: '',
  loading: '',
  submitting: '',
  submitted: 'You can close this page.',
  already_done: 'Not you? Open the chat below.',
  expired: "Need to answer now? Open the chat and we'll record it by hand.",
  superseded: '',
  all_caught_up: '',
  not_found: 'Check the link in your text message, or open the chat.',
  cancelled: 'Questions about rebooking? Open the chat.',
  error: 'Open the chat if this keeps happening.',
};

export default function App() {
  const token = useMemo(
    () => extractTokenFromPath(window.location.pathname),
    [],
  );

  const { state, bookings, submittedActions, submit } = useConfirmation(token);

  const submitting = state === 'submitting';

  // Build the receipt entries from the submission map, preserving the order
  // bookings appeared on the BookingCard so the receipt list reads the same
  // way the user filled it out. Each entry carries its own action — the
  // SubmittedScreen renders per-row outcomes (a 'happened' yes never gets
  // mislabelled because a sibling was a no).
  const submittedEntries = bookings
    .filter((b) => submittedActions[b.id] !== undefined)
    .map((b) => ({ booking: b, action: submittedActions[b.id] }));

  let view: ReactElement;
  if (state === 'loading') {
    view = <LoadingScreen />;
  } else if (state === 'ready' || state === 'submitting') {
    // While submitting we keep BookingCard mounted so the overlay spinner sits
    // on top of the card rather than popping to a new screen.
    view = bookings.length > 0 ? (
      <BookingCard
        bookings={bookings}
        submitting={submitting}
        submittedActions={submittedActions}
        onSubmit={submit}
      />
    ) : (
      <LoadingScreen />
    );
  } else if (state === 'submitted') {
    view = <SubmittedScreen entries={submittedEntries} />;
  } else if (state === 'already_done') {
    view = <AlreadyDoneScreen />;
  } else if (state === 'expired') {
    view = <ExpiredScreen />;
  } else if (state === 'superseded') {
    view = <SupersededScreen />;
  } else if (state === 'all_caught_up') {
    view = <AllCaughtUpScreen />;
  } else if (state === 'cancelled') {
    view = <CancelledScreen />;
  } else if (state === 'not_found') {
    view = <NotFoundScreen />;
  } else {
    view = <ErrorScreen />;
  }

  const helper = HELPER_BY_STATE[state];

  return (
    <>
      <SiteHeader />
      <main className="page" role="main">
        <div className="stage" key={state}>
          {view}
        </div>
        {helper && <p className="helper">{helper}</p>}
      </main>
      <SiteFooter />
    </>
  );
}
